import * as Dialog from '@radix-ui/react-dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import toast from 'react-hot-toast';
import { useState } from 'react';
import socialMediaService from '../../services/socialMediaCommonApi';
import { useSocialMediaContext } from '../../SocialMediaContext';

interface SaveDialogProps {
  isSaveDialogOpen: boolean;
  setIsSaveDialogOpen: (open: boolean) => void;
  postTitle: string;
  setPostTitle: (title: string) => void;
  platformName: string;
}

const SaveDialog = ({ 
  isSaveDialogOpen, 
  setIsSaveDialogOpen,
  postTitle,
  setPostTitle,
  platformName
}: SaveDialogProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { 
    socialMediaContent, 
    updateSocialMediaContent,
    formData // Get form data from context
  } = useSocialMediaContext();
  
  // Get the current content and image from the socialMediaContent
  const currentContent = socialMediaContent && socialMediaContent.length > 0 
    ? socialMediaContent[0].markdownContent // Use markdownContent instead of content
    : '';
  
  const currentImage = socialMediaContent && socialMediaContent.length > 0 
    ? socialMediaContent[0].image[0] 
    : '';
  console.log("currentImage",currentImage);
  const handleSave = async () => {
    if (!postTitle.trim()) {
      toast.error('Please enter a post title');
      return;
    }

    if (!formData) {
      toast.error('Missing form data');
      return;
    }

    setIsSaving(true);
    try {
      const response = await socialMediaService.saveGeneration({
        post_name: postTitle,
        post_content: currentContent,
        platform_name: platformName,
        img_url: currentImage,
        erase_img_after_publish: false,
        // Add form data fields
        post_topic: formData.topic,
        post_sub_topic: formData.subTopic,
        post_prompt: formData.description,
        post_language: formData.language,
        post_no_of_drafts: formData.drafts,
        post_tone: formData.tone
      });

      if (response.success) {
        // Update the social media content with the post ID
        if (socialMediaContent && socialMediaContent.length > 0) {
          const updatedContent = socialMediaContent.map(content => ({
            ...content,
            postId: response.data.id
          }));
          updateSocialMediaContent(updatedContent);
        }

        toast.success('Post saved successfully');
        setIsSaveDialogOpen(false);
      } else {
        toast.error(response.message || 'Failed to save post');
      }
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('An error occurred while saving the post');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog.Root open={isSaveDialogOpen} onOpenChange={setIsSaveDialogOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed text-text-primary left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-background p-6 shadow-lg w-[400px] max-w-[calc(100%-32px)]">
          <div className="flex flex-col gap-4">
            <Dialog.Title className="text-2xl font-bold text-text-primary">
              Save Post
            </Dialog.Title>

            <div className="space-y-2">
              <label className="text-sm">Post title</label>
              <Input
                value={postTitle}
                onChange={(e) => setPostTitle(e.target.value)}
                placeholder="Enter post title"
              />
            </div>

            <div className="flex justify-end gap-2 mt-2">
              <Button
                onClick={() => setIsSaveDialogOpen(false)}
                variant="customSecondary"
                size="sm"
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="customPrimary"
                size="sm"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default SaveDialog;
